import { Component, OnDestroy, OnInit, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { parseAsLocalDate } from '../../../../shared/_utils/date-utils';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  F1065K2DataEntryService,
  ColumnHeaderData,
  LineData,
  GroupedFormLine,
  FormLineItem,
  FilterParams,
  ClobSettingsObj
} from '../f1065-k2-data-entry.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-f1065-k2-p10s3',
    templateUrl: './f1065-k2-p10s3.component.html',
    styleUrls: ['../f1065-k2.styles.scss', './f1065-k2-p10s3.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F1065K2P10S3Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private lastContextKey = '';

  // Configuration - unique per component
  private lineDetailsActionCode = 'w5bepp';
  private linkPart = '_P10_S3';
  sectionHeader = 'Section 3 - Allocation and Apportionment Methods for Deductions:';

  // Data
  baseURLs: any = {};
  globalService: any = {};
  filterParams: FilterParams = {};
  GENERAL_CONFIG: any = {};
  
  colHeaderData: ColumnHeaderData[] = [];
  lineData: LineData[] = [];
  groupedFormLines: GroupedFormLine[] = [];
  
  // State
  loadingData = true;
  showLoader = true;
  isAdmin = false;
  isSaveClicked = false;
  showErrCol = false;
  isUIErrorSec = '';
  isValidationError = false;
  isInitialized = false;

  // Counters for dropdown loading
  totalDropDownActions = 0;
  loadedActionCounter = 0;

  // Labels
  currentEntityName = '';
  currentPartnerName = '';
  currentCodeGroup = '';
  formSchd = '';

  // Keys
  jcdKey = 0;
  scenario = '';

  // Confirmation modal
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Save Data',
    message: 'Are you sure to save data?',
    iconClass: 'fa fa-save',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  };

  private isLoading = false;

  constructor(
    private service: F1065K2DataEntryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwWorkspace.refreshGridData.subscribe(() => {
      this.lastContextKey = '';
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    // Explicit initial call — tryInit() has internal dedup (lastContextKey + isLoading),
    // so redundant calls from BehaviorSubject replay are harmless.
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const generalConfig = generalConfigFromGlobal(this.gtwGlobal);
    const baseUrl = generalConfig?.base_url || generalConfig?.api || '';
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};

    this.baseURLs = { ...(this.baseURLs || {}), api: baseUrl };
    this.GENERAL_CONFIG = { ...generalConfig, base_url: baseUrl };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen) || {};
    this.scenario = this.filterParams?.scenario || '';
    this.jcdKey = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || 0;
  }

  private tryInit(): void {
    if (this.isLoading) return;
    
    const hasBaseUrl = this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    
    if (!hasBaseUrl || !hasFilterParams) {
      return;
    }

    const contextKey = [
      this.GENERAL_CONFIG?.base_url || '',
      this.filterParams?.scenario || '',
      this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '',
      this.filterParams?.p_form || '',
      this.filterParams?.p_schedule || '',
      this.gtwWorkspace.workspaceFactory?.activeScreen?.link || ''
    ].join('|');

    if (contextKey === this.lastContextKey) {
      return;
    }

    this.lastContextKey = contextKey;

    if (this.isInitialized) {
      this.reloadData();
      return;
    }

    console.log('[F1065K2-P10S3] All inputs ready, initializing...');
    this.isInitialized = true;
    this.isLoading = true;
    this.init();
  }

  private reloadData(): void {
    console.log('[F1065K2-P10S3] reloadData() called');
    this.isLoading = true;
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.colHeaderData = [];
    this.lineData = [];
    this.groupedFormLines = [];
    this.totalDropDownActions = 0;
    this.loadedActionCounter = 0;
    this.init();
  }

  private init(): void {
    console.log('[F1065K2-P10S3] init() - Starting data load');
    this.checkLockStatus();
    this.loadColumnHeaders();
  }

  private checkLockStatus(): void {
    if (!this.GENERAL_CONFIG?.base_url) return;

    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        if (data?.[0]?.LOCK_STATUS === 'Y') {
          this.isAdmin = false;
          this.service.showError(`Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`);
        }
      },
      error: (err) => console.error('[F1065K2-P10S3] Lock status error:', err)
    });
  }

  private setCurrentCodeGroup(): void {
    this.currentCodeGroup = `${this.filterParams.p_form}_${this.filterParams.p_schedule}${this.linkPart}`;
    this.formSchd = `${this.filterParams.p_form}_${this.filterParams.p_schedule}`;
  }

  private setLabelDetails(): void {
    this.currentEntityName = this.filterParams.p_filer_label || '';
    if (this.currentCodeGroup.includes('_K3')) {
      this.currentPartnerName = this.filterParams.p_filer_partner_label || '';
    } else {
      this.currentPartnerName = '';
    }
  }

  private loadColumnHeaders(): void {
    this.loadingData = false;  // Switch from arrow to spinner
    this.showLoader = true;
    this.cdr.detectChanges();
    this.setCurrentCodeGroup();
    this.setLabelDetails();

    const taxYear = this.filterParams.tax_year || '';

    this.service.getColumnHeaders(
      this.GENERAL_CONFIG.base_url,
      taxYear,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        if (!data || data.length === 0) {
          this.service.showWarning('Data Error: No results were returned');
          this.loadingData = true;
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.colHeaderData = data;
        let isDropdownAvailable = false;
        this.totalDropDownActions = 0;
        this.loadedActionCounter = 0;

        for (const col of this.colHeaderData) {
          this.setValidationInfo(col, col);
          if (col.DROP_DOWN_ACTION) {
            isDropdownAvailable = true;
            this.totalDropDownActions++;
            this.loadDropdownData(col);
          }
        }

        if (!isDropdownAvailable) {
          this.loadLineDetails();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-P10S3] Column headers error:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private setValidationInfo(colHeader: any, destObj: any): void {
    if (colHeader.VALIDATION_RULE) {
      const validationRulesArr = colHeader.VALIDATION_RULE.split(';');
      for (const rule of validationRulesArr) {
        if (rule === 'REQ') {
          destObj.IS_MANDATORY = 'Y';
        } else if (rule.startsWith('PREF_REQ')) {
          destObj.IS_PREF_MANDATORY = 'Y';
          if (rule.includes(':') && rule.includes('/')) {
            destObj.PREF_MAN_COLS = rule.split(':')[1].split('/');
            destObj.VALIDATION_MSG = destObj.PREF_MAN_COLS[destObj.PREF_MAN_COLS.length - 1];
            destObj.PREF_MAN_COLS.splice(destObj.PREF_MAN_COLS.length - 1, 1);
          }
        } else if (rule.includes('MAX_LEN:')) {
          destObj.MAX_LENGTH = parseInt(rule.split(':')[1], 10);
        }
      }
    }
  }

  private loadDropdownData(colData: ColumnHeaderData): void {
    const actionDetails = colData.DROP_DOWN_ACTION!.split(':');
    const actionCode = actionDetails[0];
    const codeGroup = actionDetails[1];
    const taxYear = this.filterParams.tax_year || '';

    this.service.getDropdownData(
      this.GENERAL_CONFIG.base_url,
      actionCode,
      codeGroup,
      taxYear,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        colData.actionData = data;
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-P10S3] Dropdown error:', err);
        this.loadedActionCounter++;
        if (this.loadedActionCounter === this.totalDropDownActions) {
          this.loadLineDetails();
        }
        this.cdr.detectChanges();
      }
    });
  }

  private loadLineDetails(): void {
    this.showLoader = true;

    const filerKey = this.filterParams.p_filer_key;
    const hasFilerKey = filerKey !== undefined && filerKey !== null && filerKey !== '' && 
                        (typeof filerKey === 'string' ? filerKey.trim().length > 0 : (filerKey as number) >= 0);

    if (!hasFilerKey) {
      this.service.showError('Please select all the filters');
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    if (this.currentCodeGroup.includes('_K3')) {
      const partnerKey = this.filterParams.p_filer_partner_key;
      const hasPartnerKey = partnerKey !== undefined && partnerKey !== null && 
                            (typeof partnerKey === 'string' ? partnerKey.trim().length > 0 : (partnerKey as number) >= 0) &&
                            partnerKey !== -1 && partnerKey !== '-1';
      if (!hasPartnerKey) {
        this.service.showError('Please select all the filters');
        this.isLoading = false;
        this.cdr.detectChanges();
        return;
      }
    }

    this.service.getLineDetails(
      this.GENERAL_CONFIG.base_url,
      this.lineDetailsActionCode,
      this.filterParams.tax_year || '',
      filerKey!,
      this.currentCodeGroup,
      this.globalService?.globalParams || {},
      this.filterParams,
      this.filterParams.p_filer_partner_key
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.lineData = data || [];
        this.groupedFormLines = this.processData(this.lineData);
        
        // Calculate totals (empty for this component)
        for (const group of this.groupedFormLines) {
          this.calcTotalsCol(group.items);
        }
        
        this.showLoader = false;
        this.loadingData = false;
        this.showErrCol = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[F1065K2-P10S3] Line details error:', err);
        this.showLoader = false;
        this.loadingData = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private processData(formLines: LineData[]): GroupedFormLine[] {
    if (formLines && formLines.length > 0) {
      this.isAdmin = true;
    }

    return _.reduce(
      formLines,
      (memo: GroupedFormLine[], property: LineData) => {
        let bucket = _.find(memo, {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          LINE_NO: property.LINE_NO,
          LINE_DESC: property.LINE_DESC,
          LINE_ORDER: property.LINE_ORDER,
          IS_DISPLAY: 'Y',
          SNO: property.SNO,
          IS_LINE_MULTI: property.IS_LINE_MULTI,
          LINE_SEC: property.LINE_SEC
        });

        if (!bucket) {
          bucket = {
            SCHD_KEY: property.SCHD_KEY,
            LINE_KEY: property.LINE_KEY,
            LINE_NO: property.LINE_NO,
            LINE_DESC: property.LINE_DESC || '',
            LINE_ORDER: property.LINE_ORDER,
            IS_DISPLAY: 'Y',
            SNO: property.SNO,
            IS_LINE_MULTI: property.IS_LINE_MULTI,
            LINE_SEC: property.LINE_SEC,
            items: []
          };
          memo.push(bucket);
        }

        const attribs: FormLineItem = {
          SCHD_KEY: property.SCHD_KEY,
          LINE_KEY: property.LINE_KEY,
          COL_KEY: property.COL_KEY,
          COL_DESC: property.COL_DESC,
          COL_ORDER: property.COL_ORDER,
          IS_ENABLED: property.EDITABLE_Y_N,
          SNO: property.SNO,
          ADJ_KEY: property.ADJ_KEY,
          IS_COL_MULTI: property.IS_COL_MULTI,
          UI_COMP: property.UI_COMP,
          ACTION_Y_N: property.ACTION_Y_N,
          IS_MANDATORY: 'N',
          CELL_VALUE: property.CELL_VALUE
        };

        if (attribs.ACTION_Y_N === 'Y') {
          const colObj = this.colHeaderData.find(col => col.COL_KEY === attribs.COL_KEY);
          if (colObj) {
            attribs.actionData = colObj.actionData;
          }
        }

        this.setValidationInfo(property, attribs);

        if (attribs.ADJ_KEY !== undefined && attribs.ADJ_KEY !== null) {
          attribs.OPERATION_TYPE = 'U';
        } else {
          attribs.OPERATION_TYPE = undefined;
        }

        if (property.IS_LINE_MULTI === 'Y' && this.isEmptyVal(attribs.CELL_VALUE)) {
          this.setUICompDefaults(attribs);
        }

        if (attribs.UI_COMP === 'DATE' && this.isUICompNotEmpty(attribs)) {
          attribs.CELL_VALUE = parseAsLocalDate(property.CELL_VALUE);
        }

        if (this.isSkipFromSaving(bucket, attribs)) {
          attribs.OPERATION_TYPE = 'N';
        }

        bucket.items.push(attribs);
        return memo;
      },
      []
    );
  }

  private isSkipFromSaving(bucket: GroupedFormLine, attribs: FormLineItem): boolean {
    return attribs.IS_ENABLED === 'N';
  }

  private setUICompDefaults(attrib: FormLineItem): void {
    if (attrib.UI_COMP === 'NUMERIC') {
      attrib.CELL_VALUE = null;
    } else if (['TEXT', 'DATE', 'CHK_BX', 'DROP_DOWN'].includes(attrib.UI_COMP)) {
      attrib.CELL_VALUE = '';
    }
  }

  private isUICompEmpty(attrib: FormLineItem): boolean {
    if (attrib.CELL_VALUE === undefined || attrib.CELL_VALUE === null) {
      return true;
    }
    if (attrib.UI_COMP === 'NUMERIC' && attrib.CELL_VALUE === null) {
      return true;
    }
    if (['TEXT', 'DATE', 'CHK_BX', 'DROP_DOWN'].includes(attrib.UI_COMP) && attrib.CELL_VALUE === '') {
      return true;
    }
    return false;
  }

  private isUICompNotEmpty(attrib: FormLineItem): boolean {
    return !this.isUICompEmpty(attrib);
  }

  private isEmptyVal(val: any): boolean {
    return val === null || val === '' || val === undefined;
  }

  // --- UI Actions ---

  addRow(selectedRowData: GroupedFormLine): void {
    const index = this.groupedFormLines.indexOf(selectedRowData);
    const tempObj = _.cloneDeep(selectedRowData);
    const multiRow = this.groupedFormLines.filter(
      row => row.LINE_KEY === selectedRowData.LINE_KEY && row.IS_DISPLAY === 'Y'
    );
    const sno = multiRow[multiRow.length - 1].SNO + 1;

    tempObj.LINE_DESC = '';
    tempObj.LINE_NO = '';
    tempObj.ERR_MSG = undefined;
    tempObj.SNO = sno;

    for (const item of tempObj.items) {
      item.OPERATION_TYPE = 'I';
      item.ADJ_KEY = null;
      this.setUICompDefaults(item);
      item.SNO = tempObj.SNO;
    }

    this.groupedFormLines.splice(index + sno + 1, 0, tempObj);
    this.cdr.detectChanges();
  }

  deleteRow(selectedRowData: GroupedFormLine): void {
    let isRowSaved = false;
    for (const item of selectedRowData.items) {
      if (item.OPERATION_TYPE === 'U') {
        item.OPERATION_TYPE = 'D';
        selectedRowData.IS_DISPLAY = 'N';
        isRowSaved = true;
      }
    }
    if (!isRowSaved) {
      const idx = this.groupedFormLines.indexOf(selectedRowData);
      this.groupedFormLines.splice(idx, 1);
    }
    this.cdr.detectChanges();
  }

  calcTotalsCol(columnValues: FormLineItem[]): void {
    // Empty for P10S3 - no column totals calculation
  }

  /**
   * Check if item is the last NUMERIC column
   * P10S3 doesn't have auto-calculated totals, so always return false
   */
  isLastNumericColumn(items: FormLineItem[], item: FormLineItem): boolean {
    return false; // P10S3 has no auto-calculated Total column
  }

  /**
   * Handle keypress for NUMERIC inputs - only allow digits and minus at start
   */
  onNumericKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart || 0;

    // Allow digits 0-9
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }

    // Allow minus sign only at the start (position 0) and if not already present
    if (charStr === '-') {
      if (selectionStart === 0 && !currentValue.includes('-')) {
        return true;
      }
      return false;
    }

    // Block all other characters (including decimal point)
    return false;
  }

  /**
   * Handle paste for NUMERIC inputs - filter to only allowed characters
   */
  onNumericPaste(event: ClipboardEvent, item: FormLineItem): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    let filtered = pastedText.replace(/[^0-9-]/g, '');
    if (filtered.includes('-')) {
      const hasMinusAtStart = filtered.startsWith('-');
      filtered = filtered.replace(/-/g, '');
      if (hasMinusAtStart) {
        filtered = '-' + filtered;
      }
    }
    item.CELL_VALUE = filtered ? parseInt(filtered, 10) : null;
    this.cdr.detectChanges();
  }

  // --- Save ---

  save(): void {
    this.isSaveClicked = true;
    this.validateForm(this.groupedFormLines);

    if (this.isValidationError) {
      this.service.showError('Please fix all error(s)');
      this.isSaveClicked = false;
      return;
    }

    // Update confirmation message with entity name
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure to save data for: ${this.currentEntityName}${this.currentPartnerName ? ' / ' + this.currentPartnerName : ''}`
    };
    
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  onSaveConfirm(): void {
    this.showConfirmModal = false;
    const dataToSave: any[] = [];
    this.pushToDataToSave(this.groupedFormLines, dataToSave);

    const returnClobSettingsObj: ClobSettingsObj = {
      jcd_key: this.jcdKey,
      p_form_schd: this.formSchd,
      p_code_group: this.currentCodeGroup
    };

    // Call getLockStatus before save (matching original AngularJS behavior)
    this.service.getLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.filterParams.tax_year || '',
      this.jcdKey,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Proceed with save after lock status check
        this.service.saveData(
          this.GENERAL_CONFIG.base_url,
          returnClobSettingsObj,
          dataToSave,
          this.globalService?.globalParams || {},
          this.filterParams,
          this.globalService?.inputs?.tax_year?.values || []
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (result: any) => {
            // Use loose comparison (!=) like original AngularJS: result.data.callSuccess != 1
            if (result?.callSuccess != 1) {
              // Handle errorMessage: "null" string from API
              const errorMsg = (result?.errorMessage && result.errorMessage !== 'null') 
                ? result.errorMessage 
                : 'Save failed';
              this.service.showError(errorMsg);
            } else {
              this.service.showSuccess('Data have been saved/updated');
            }
            this.isSaveClicked = false;
            this.showErrCol = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('[F1065K2-P10S3] Save error:', err);
            this.service.showError('Save operation failed');
            this.isSaveClicked = false;
            this.cdr.detectChanges();
          }
        });
      },
      error: (err) => {
        console.error('[F1065K2-P10S3] Lock status check error:', err);
        this.isSaveClicked = false;
        this.cdr.detectChanges();
      }
    });
  }

  onSaveCancel(): void {
    this.showConfirmModal = false;
    this.isSaveClicked = false;
    this.cdr.detectChanges();
  }

  reset(): void {
    this.reloadData();
  }

  // --- Validation ---

  private validateForm(groupedFormLinesObj: GroupedFormLine[]): void {
    this.isValidationError = false;
    this.isUIErrorSec = '';

    for (const line of groupedFormLinesObj) {
      line.ERR_MSG = undefined;
      let requiredMsgAlreadyAdded = false;

      // Reset errors
      for (const item of line.items) {
        item.IS_ERROR = 'N';
      }

      for (const item of line.items) {
        if (line.IS_DISPLAY === 'Y' && item.IS_ENABLED !== 'N') {
          // Mandatory validation
          if (item.IS_MANDATORY === 'Y' && this.isUICompEmpty(item)) {
            this.setErrorAttribs(line, item, 'Mandatory field(s) are missing', requiredMsgAlreadyAdded);
            requiredMsgAlreadyAdded = true;
          }
        }
      }
    }

    // Update error section flags
    if (this.isUIErrorSec.length > 0) {
      if (this.isUIErrorSec.includes('UI_S1')) {
        this.isUIErrorSec += 'UI_S1';
      }
      if (this.isUIErrorSec.includes('UI_S2')) {
        this.isUIErrorSec += 'UI_S2';
      }
    }
  }

  private setErrorAttribs(line: GroupedFormLine, col: FormLineItem | null, errMsg: string, requiredMsgAlreadyAdded: boolean): void {
    if (!line.ERR_MSG) {
      line.ERR_MSG = '<ol></ol>';
      this.isUIErrorSec += ',' + (line.LINE_SEC || '');
    }
    if (col) {
      col.IS_ERROR = 'Y';
    }
    this.isValidationError = true;
    if (!requiredMsgAlreadyAdded) {
      line.ERR_MSG = line.ERR_MSG.replace('</ol>', '<li>' + errMsg + '</li></ol>');
    }
    this.showErrCol = true;
  }

  private pushToDataToSave(groupedFormLinesObj: GroupedFormLine[], dataToSave: any[]): void {
    for (const line of groupedFormLinesObj) {
      for (const item of line.items) {
        const returnObj: any = {};
        let cellValue = item.CELL_VALUE;

        if (item.IS_ENABLED !== 'N') {
          const itemAny = item as any;
          const comp = itemAny.LINE_COMP || item.UI_COMP;
          
          if (comp === 'DATE') {
            if (this.isUICompEmpty(item) || (this.isUICompNotEmpty(item) && cellValue instanceof Date && isNaN(cellValue.getTime()))) {
              returnObj.CELL_VALUE = '';
            } else if (cellValue instanceof Date) {
              const tempDate = new Date(cellValue);
              returnObj.CELL_VALUE = `${tempDate.getFullYear()}-${tempDate.getMonth() + 1}-${tempDate.getDate()}`;
            } else {
              returnObj.CELL_VALUE = cellValue;
            }
          } else if (comp === 'NUMERIC') {
            // NUMERIC fields: check if this is the Total column (string with decimals like "1244.0000")
            if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
              if (typeof cellValue === 'string' && cellValue.includes('.') && /\.\d{4}$/.test(cellValue)) {
                // Keep as string for Total column
                returnObj.CELL_VALUE = cellValue;
              } else {
                // Convert to number for regular NUMERIC fields
                returnObj.CELL_VALUE = Number(cellValue);
              }
            } else {
              returnObj.CELL_VALUE = null;
            }
          } else if (comp === 'CHK_BX') {
            // Checkboxes: send "X" if checked, "" if not (matching original AngularJS)
            returnObj.CELL_VALUE = (cellValue === true || cellValue === 'X' || cellValue === '1') ? 'X' : '';
          } else if (comp === 'DROP_DOWN') {
            // Dropdowns: empty value should be null (matching original AngularJS)
            if (cellValue === null || cellValue === undefined || cellValue === '') {
              returnObj.CELL_VALUE = null;
            } else {
              returnObj.CELL_VALUE = cellValue;
            }
          } else {
            returnObj.CELL_VALUE = cellValue;
          }
        }

        returnObj.SCHD_KEY = item.SCHD_KEY;
        returnObj.LINE_KEY = item.LINE_KEY;
        returnObj.COL_KEY = item.COL_KEY;
        returnObj.ADJ_KEY = item.ADJ_KEY;
        returnObj.SNO = item.SNO;
        returnObj.OPERATION_TYPE = item.OPERATION_TYPE;

        if (item.OPERATION_TYPE === undefined || item.OPERATION_TYPE === null) {
          returnObj.OPERATION_TYPE = 'I';
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'I') {
          item.OPERATION_TYPE = 'U';
        } else if (item.OPERATION_TYPE === 'N') {
          returnObj.OPERATION_TYPE = null;
        }

        if (returnObj.OPERATION_TYPE !== null) {
          dataToSave.push(returnObj);
        }
      }
    }
  }

  // --- Filter helpers for template ---

  // COL_SEC can be comma-separated (e.g., "UI_S1,UI_S2") so we use includes()
  getColHeadersForSection(section: string): ColumnHeaderData[] {
    return this.colHeaderData
      .filter(col => col.COL_SEC?.includes(section))
      .sort((a, b) => (a.COL_PRIORITY || 0) - (b.COL_PRIORITY || 0));
  }

  getRowsForSection(section: string): GroupedFormLine[] {
    return this.groupedFormLines
      .filter(row => row.IS_DISPLAY === 'Y' && row.LINE_SEC === section)
      .sort((a, b) => {
        const orderDiff = (a.LINE_ORDER || 0) - (b.LINE_ORDER || 0);
        return orderDiff !== 0 ? orderDiff : (a.SNO || 0) - (b.SNO || 0);
      });
  }

  trackByRow(index: number, row: GroupedFormLine): string {
    return `${row.LINE_KEY}_${row.SNO}_${row.IS_DISPLAY}`;
  }

  trackByCol(index: number, col: FormLineItem): string {
    return `${col.COL_KEY}_${col.SNO}`;
  }
}
